import { useId, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  applyStorefrontFilters,
  filterValues,
  productPrice,
} from "../../utils/storefrontFilters";

function OptionList({ title, options, selected, onToggle, counts }) {
  const [search, setSearch] = useState(""),
    [expanded, setExpanded] = useState(false);
  const matches = options
    .filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase()),
    )
    .sort(
      (a, b) =>
        Number(selected.includes(b.value)) -
          Number(selected.includes(a.value)) ||
        Number((counts[b.value] || 0) > 0) -
          Number((counts[a.value] || 0) > 0) ||
        a.label.localeCompare(b.label, undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    );
  const shown = expanded || search ? matches : matches.slice(0, 8);
  return (
    <section className="border-b border-slate-200 py-5">
      <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-900">
        {title}
      </h3>
      {options.length > 5 && (
        <input
          aria-label={"Search " + title.toLowerCase()}
          placeholder={"Search " + title.toLowerCase()}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="mb-3 h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-primary-800"
        />
      )}
      <div className="max-h-64 space-y-2 overflow-y-auto overscroll-contain">
        {shown.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
          >
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={() => onToggle(option.value)}
              className="h-4 w-4 accent-primary-800"
            />
            {option.color && (
              <span
                className="h-4 w-4 rounded-full border border-slate-200"
                style={{ backgroundColor: option.color }}
              />
            )}
            <span className="min-w-0 flex-1">{option.label}</span>
            <span className="text-xs text-slate-400">
              ({counts[option.value] || 0})
            </span>
          </label>
        ))}
      </div>
      {!matches.length && (
        <p className="text-xs text-slate-400">No matching options</p>
      )}
      {!search && options.length > 8 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-sm font-semibold text-primary-800"
        >
          {expanded ? "Show less" : "+ " + (options.length - 8) + " more"}
        </button>
      )}
    </section>
  );
}
export default function FiltersSidebar({
  filters,
  setFilters,
  products = [],
  categories = [],
  onCategoryChange,
  showHeader = true,
}) {
  const discountGroup = useId();
  const options = useMemo(() => {
    const unique = (values) =>
      [...new Set(values.filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true }),
      );
    const selectedCategories = filterValues(
      filters,
      "categoryIds",
      "categoryId",
    );
    const subCategories = categories
      .filter(
        (category) =>
          !selectedCategories.length ||
          selectedCategories.includes(category.id),
      )
      .flatMap((category) =>
        (category.subCategories || []).map((sub) => ({
          value: sub.id,
          label:
            sub.name +
            (selectedCategories.length === 1 ? "" : " — " + category.name),
        })),
      );
    const variants = products.flatMap((product) => product.variants || []);
    const colors = [
      ...new Map(
        variants
          .filter((v) => v.color?.name)
          .map((v) => [
            v.color.name,
            {
              value: v.color.name,
              label: v.color.name,
              color: v.color.hexCode || "#d1d5db",
            },
          ]),
      ).values(),
    ];
    return {
      categoryIds: categories.map((category) => ({
        value: category.id,
        label: category.name,
      })),
      subCategoryIds: subCategories,
      brands: unique(
        products.map((product) => product.brand?.name || product.brand),
      ).map((value) => ({ value, label: value })),
      sizes: unique(variants.map((v) => v.size?.name || v.size?.code)).map(
        (value) => ({ value, label: value }),
      ),
      colors,
    };
  }, [categories, products, filters.categoryIds, filters.categoryId]);
  const selected = {
    categoryIds: filterValues(filters, "categoryIds", "categoryId"),
    subCategoryIds: filters.subCategoryIds || [],
    brands: filterValues(filters, "brands", "brand"),
    sizes: filterValues(filters, "sizes", "size"),
    colors: filterValues(filters, "colors", "color"),
  };
  const legacy = {
    categoryIds: "categoryId",
    subCategoryIds: "subCategorySlug",
    brands: "brand",
    sizes: "size",
    colors: "color",
  };
  const toggle = (key, value) => {
    if (key === "categoryIds" && onCategoryChange) {
      setFilters((current) => ({
        ...current,
        categoryIds: [],
        categoryId: "",
        subCategoryIds: [],
        subCategorySlug: "",
      }));
      onCategoryChange(selected.categoryIds.includes(value) ? "" : value);
      return;
    }
    setFilters((current) => {
      const values = filterValues(current, key, legacy[key]);
      const next = values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value];
      return {
        ...current,
        [key]: next,
        [legacy[key]]: "",
        ...(key === "categoryIds"
          ? { subCategoryIds: [], subCategorySlug: "" }
          : {}),
      };
    });
  };
  const counts = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(options).map((key) => {
          const candidates = applyStorefrontFilters(products, filters, key);
          return [
            key,
            Object.fromEntries(
              options[key].map((option) => {
                const trial = {
                  ...filters,
                  [key]: [option.value],
                  [legacy[key]]: "",
                };
                if (key === "categoryIds") trial.subCategoryIds = [];
                return [
                  option.value,
                  applyStorefrontFilters(candidates, trial).length,
                ];
              }),
            ),
          ];
        }),
      ),
    [options, products, filters],
  );
  const maxPrice = Math.max(
    1,
    ...products.map(productPrice).filter(Number.isFinite),
  );
  const minimum = Number(filters.minPrice || 0),
    maximum =
      filters.maxPrice === "" || filters.maxPrice == null
        ? maxPrice
        : Number(filters.maxPrice);
  const update = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const chips = Object.keys(selected).flatMap((key) =>
    selected[key].map((value) => ({
      key,
      value,
      label:
        options[key].find((option) => option.value === value)?.label || value,
    })),
  );
  return (
    <div className="px-1">
      {showHeader && (
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <span className="text-sm font-bold text-primary-900">FILTERS</span>
          <button
            type="button"
            onClick={() =>
              setFilters({
                minPrice: "",
                maxPrice: "",
                discount: 0,
                rating: 0,
                categoryIds: [],
                subCategoryIds: [],
                brands: [],
                sizes: [],
                colors: [],
                categoryId: "",
                subCategorySlug: "",
                brand: "",
                size: "",
                color: "",
              })
            }
            className="text-xs font-bold text-primary-800"
          >
            CLEAR ALL
          </button>
        </div>
      )}
      {(chips.length > 0 ||
        minimum > 0 ||
        filters.maxPrice ||
        filters.discount > 0) && (
        <div className="flex flex-wrap gap-2 border-b border-slate-200 py-4">
          {chips.map((chip) => (
            <button
              key={chip.key + chip.value}
              type="button"
              onClick={() => toggle(chip.key, chip.value)}
              className="flex items-center gap-1 rounded-full border border-primary-100 bg-primary-50 px-2 py-1 text-xs text-primary-800"
            >
              {chip.label}
              <X size={12} />
            </button>
          ))}
          {minimum > 0 && (
            <button
              onClick={() => update("minPrice", "")}
              className="rounded-full border px-2 py-1 text-xs"
            >
              From ₹{minimum} ×
            </button>
          )}
          {filters.maxPrice !== "" && filters.maxPrice != null && (
            <button
              onClick={() => update("maxPrice", "")}
              className="rounded-full border px-2 py-1 text-xs"
            >
              Up to ₹{maximum} ×
            </button>
          )}
          {filters.discount > 0 && (
            <button
              onClick={() => update("discount", 0)}
              className="rounded-full border px-2 py-1 text-xs"
            >
              {filters.discount}% off & above ×
            </button>
          )}
        </div>
      )}
      {["categoryIds", "subCategoryIds", "brands"].map((key, index) => (
        <OptionList
          key={key}
          title={["Categories", "Subcategories", "Brand"][index]}
          options={options[key]}
          selected={selected[key]}
          onToggle={(value) => toggle(key, value)}
          counts={counts[key]}
        />
      ))}
      <section className="border-b border-slate-200 py-5">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wider">
          Price
        </h3>
        <p className="mb-3 text-sm text-primary-800">
          ₹{minimum.toLocaleString("en-IN")} – ₹
          {maximum.toLocaleString("en-IN")}
        </p>
        <label className="block text-xs text-slate-500">
          Minimum price
          <input
            aria-label="Minimum price slider"
            type="range"
            min="0"
            max={maxPrice}
            step="1"
            value={Math.min(minimum, maxPrice)}
            onChange={(event) =>
              update("minPrice", Math.min(Number(event.target.value), maximum))
            }
            className="w-full accent-primary-800"
          />
        </label>
        <label className="block text-xs text-slate-500">
          Maximum price
          <input
            aria-label="Maximum price slider"
            type="range"
            min="0"
            max={maxPrice}
            step="1"
            value={Math.min(maximum, maxPrice)}
            onChange={(event) =>
              update("maxPrice", Math.max(Number(event.target.value), minimum))
            }
            className="w-full accent-primary-800"
          />
        </label>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <input
            aria-label="Minimum price"
            type="number"
            min="0"
            value={filters.minPrice ?? ""}
            placeholder="Min ₹"
            onChange={(event) => update("minPrice", event.target.value)}
            className="w-full rounded-lg border border-slate-200 p-2 text-sm"
          />
          <input
            aria-label="Maximum price"
            type="number"
            min="0"
            value={filters.maxPrice ?? ""}
            placeholder="Max ₹"
            onChange={(event) => update("maxPrice", event.target.value)}
            className="w-full rounded-lg border border-slate-200 p-2 text-sm"
          />
        </div>
        {minimum > maximum && (
          <p className="mt-2 text-xs text-red-600">
            Minimum must not exceed maximum.
          </p>
        )}
      </section>
      <OptionList
        title="Colour"
        options={options.colors}
        selected={selected.colors}
        onToggle={(value) => toggle("colors", value)}
        counts={counts.colors}
      />
      <OptionList
        title="Size"
        options={options.sizes}
        selected={selected.sizes}
        onToggle={(value) => toggle("sizes", value)}
        counts={counts.sizes}
      />
      <section className="py-5">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wider">
          Discount range
        </h3>
        <div className="space-y-2">
          {[10, 20, 30, 40, 50, 60, 70, 80, 90].map((value) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
            >
              <input
                type="radio"
                name={discountGroup}
                value={value}
                checked={Number(filters.discount) === value}
                onClick={() => {
                  if (Number(filters.discount) === value) update("discount", 0);
                }}
                onChange={() => update("discount", value)}
                className="accent-primary-800"
              />
              {value}% and above
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}
